import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { Snowflake, Lock, MessageCircle, PieChart } from 'lucide-react';

const FrozenOverlay = ({ reason, until, onContactSupport, onViewPortfolio }) => {
    // Generate random snowflakes for ambient animation
    const snowflakes = useMemo(() => {
        return Array.from({ length: 28 }).map((_, i) => ({
            id: i,
            left: Math.random() * 100,
            delay: Math.random() * 8,
            duration: 8 + Math.random() * 10,
            size: 10 + Math.random() * 20,
            opacity: 0.2 + Math.random() * 0.5,
        }));
    }, []);

    const crystals = useMemo(() => {
        return Array.from({ length: 6 }).map((_, i) => ({
            id: i,
            x: -20 + i * 30,
            delay: i * 0.15,
        }));
    }, []);

    return (
        <motion.div
            className="fixed inset-0 z-[9998] overflow-hidden pointer-events-auto"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.6 }}
            data-testid="frozen-overlay"
        >
            {/* Icy gradient backdrop */}
            <div className="absolute inset-0 bg-gradient-to-br from-[#0b1a2e]/95 via-[#0a2540]/95 to-[#051624]/95" />

            {/* Aurora glow */}
            <motion.div
                className="absolute inset-0 opacity-40"
                style={{
                    background: 'radial-gradient(circle at 20% 30%, rgba(56,189,248,0.35), transparent 55%), radial-gradient(circle at 80% 70%, rgba(147,197,253,0.25), transparent 55%)',
                }}
                animate={{ opacity: [0.25, 0.5, 0.25] }}
                transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
            />

            {/* Frosted glass frame edges */}
            <div className="absolute inset-0 pointer-events-none"
                 style={{
                     boxShadow: 'inset 0 0 180px 60px rgba(186,230,253,0.18), inset 0 0 60px 10px rgba(186,230,253,0.08)',
                 }}
            />

            {/* Animated frost corners */}
            {['top-0 left-0', 'top-0 right-0 rotate-90', 'bottom-0 right-0 rotate-180', 'bottom-0 left-0 -rotate-90'].map((pos, i) => (
                <motion.div
                    key={pos}
                    className={`absolute ${pos} w-64 h-64 pointer-events-none`}
                    initial={{ opacity: 0, scale: 0.85 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.2 + i * 0.1, duration: 1.2, ease: 'easeOut' }}
                >
                    <svg viewBox="0 0 256 256" fill="none" className="w-full h-full">
                        <defs>
                            <linearGradient id={`frostGrad${i}`} x1="0" x2="1" y1="0" y2="1">
                                <stop offset="0%" stopColor="rgba(224,242,254,0.7)" />
                                <stop offset="100%" stopColor="rgba(56,189,248,0)" />
                            </linearGradient>
                        </defs>
                        <path d="M0 0 L256 0 L256 8 Q160 14 80 80 Q14 160 8 256 L0 256 Z" fill={`url(#frostGrad${i})`} />
                        <g stroke="rgba(224,242,254,0.6)" strokeWidth="0.8" fill="none">
                            <path d="M0 0 L80 40 L40 80 Z" />
                            <path d="M0 40 L60 60 L30 120" />
                            <path d="M40 0 L60 60 L120 30" />
                            <path d="M10 10 L40 40" />
                            <path d="M20 50 L50 70" />
                            <path d="M50 20 L70 50" />
                        </g>
                    </svg>
                </motion.div>
            ))}

            {/* Falling snowflakes */}
            {snowflakes.map((s) => (
                <motion.div
                    key={s.id}
                    className="absolute text-cyan-100 pointer-events-none"
                    style={{ left: `${s.left}%`, top: -40, fontSize: s.size, opacity: s.opacity }}
                    animate={{
                        y: ['0vh', '110vh'],
                        x: [0, 30, -20, 10, 0],
                        rotate: [0, 360],
                    }}
                    transition={{
                        duration: s.duration,
                        delay: s.delay,
                        repeat: Infinity,
                        ease: 'linear',
                    }}
                >
                    <Snowflake size={s.size} strokeWidth={1.2} />
                </motion.div>
            ))}

            {/* Center content card */}
            <div className="relative z-10 h-full flex items-center justify-center px-4 py-8 md:py-12 overflow-y-auto">
                <motion.div
                    initial={{ opacity: 0, scale: 0.92, y: 20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    transition={{ duration: 0.8, ease: [0.19, 1, 0.22, 1] }}
                    className="relative w-full max-w-3xl"
                >
                    {/* Animated ice crystal ring behind card */}
                    <div className="absolute inset-0 -m-10 pointer-events-none">
                        {crystals.map((c) => (
                            <motion.div
                                key={c.id}
                                className="absolute left-1/2 top-1/2"
                                style={{ translateX: '-50%', translateY: '-50%' }}
                                animate={{ rotate: 360 }}
                                transition={{ duration: 60, delay: c.delay, repeat: Infinity, ease: 'linear' }}
                            >
                                <Snowflake
                                    size={120 + c.id * 14}
                                    strokeWidth={0.5}
                                    className="text-cyan-400/15"
                                />
                            </motion.div>
                        ))}
                    </div>

                    {/* Glass card */}
                    <div
                        className="relative rounded-3xl overflow-hidden border border-cyan-300/25"
                        style={{
                            background: 'linear-gradient(135deg, rgba(186,230,253,0.12) 0%, rgba(30,58,138,0.25) 100%)',
                            backdropFilter: 'blur(24px)',
                            WebkitBackdropFilter: 'blur(24px)',
                            boxShadow: '0 25px 80px -20px rgba(14,165,233,0.35), inset 0 1px 0 rgba(255,255,255,0.15)',
                        }}
                    >
                        {/* Top badge */}
                        <div className="px-8 pt-8 md:px-12 md:pt-12">
                            <motion.div
                                initial={{ opacity: 0, y: -10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.3 }}
                                className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-cyan-400/10 border border-cyan-300/30 backdrop-blur-sm"
                            >
                                <motion.div
                                    animate={{ rotate: 360 }}
                                    transition={{ duration: 12, repeat: Infinity, ease: 'linear' }}
                                >
                                    <Snowflake size={14} className="text-cyan-200" />
                                </motion.div>
                                <span className="text-[10px] md:text-xs uppercase tracking-[0.25em] text-cyan-100 font-semibold">
                                    Account Frozen
                                </span>
                            </motion.div>
                        </div>

                        {/* Big heading */}
                        <motion.h1
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.45, duration: 0.8 }}
                            className="px-8 md:px-12 pt-6 pb-2 font-black tracking-tight leading-[0.9]"
                            style={{
                                fontSize: 'clamp(2.75rem, 8vw, 6rem)',
                                background: 'linear-gradient(180deg, #f0f9ff 0%, #7dd3fc 60%, #38bdf8 100%)',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                backgroundClip: 'text',
                                textShadow: '0 0 40px rgba(56,189,248,0.25)',
                            }}
                            data-testid="frozen-overlay-title"
                        >
                            FROZEN
                        </motion.h1>

                        {/* Reason — the main message */}
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.7, duration: 0.6 }}
                            className="px-8 md:px-12 pb-6"
                        >
                            <p
                                className="text-cyan-50/90 font-semibold leading-snug max-w-2xl"
                                style={{ fontSize: 'clamp(1.125rem, 2.5vw, 1.75rem)' }}
                                data-testid="frozen-overlay-reason"
                            >
                                {reason || 'Your account has been temporarily frozen. Please contact support for assistance.'}
                            </p>
                            {until && (
                                <p className="mt-3 text-xs md:text-sm text-cyan-200/70 uppercase tracking-wider">
                                    Auto-unfreezes · {new Date(until).toLocaleString()}
                                </p>
                            )}
                        </motion.div>

                        {/* Divider */}
                        <div className="mx-8 md:mx-12 h-px bg-gradient-to-r from-transparent via-cyan-300/40 to-transparent" />

                        {/* Footer actions */}
                        <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.9 }}
                            className="px-8 md:px-12 py-6 md:py-8 flex flex-col md:flex-row md:items-center justify-between gap-4"
                        >
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-cyan-500/15 border border-cyan-300/30 flex items-center justify-center">
                                    <Lock size={18} className="text-cyan-200" />
                                </div>
                                <div>
                                    <p className="text-[10px] md:text-xs uppercase tracking-wider text-cyan-200/70">Status</p>
                                    <p className="text-sm md:text-base font-semibold text-cyan-50">All actions suspended</p>
                                </div>
                            </div>
                            <div className="flex flex-wrap items-center gap-3">
                                <button
                                    onClick={onContactSupport}
                                    data-testid="frozen-overlay-contact-support"
                                    className="group relative px-6 py-3 rounded-xl font-bold uppercase tracking-wider text-sm transition-all overflow-hidden"
                                    style={{
                                        background: 'linear-gradient(135deg, #7dd3fc 0%, #38bdf8 100%)',
                                        color: '#0c2238',
                                        boxShadow: '0 10px 30px -10px rgba(56,189,248,0.55)',
                                    }}
                                >
                                    <span className="relative z-10 flex items-center gap-2">
                                        <MessageCircle size={16} />
                                        Contact Support
                                    </span>
                                    <span className="absolute inset-0 bg-white opacity-0 group-hover:opacity-20 transition-opacity" />
                                </button>
                                {onViewPortfolio && (
                                    <button
                                        onClick={onViewPortfolio}
                                        data-testid="frozen-overlay-view-portfolio"
                                        className="group relative px-6 py-3 rounded-xl font-bold uppercase tracking-wider text-sm transition-all overflow-hidden border border-cyan-300/40 text-cyan-100 hover:bg-cyan-400/10"
                                    >
                                        <span className="relative z-10 flex items-center gap-2">
                                            <PieChart size={16} />
                                            View Portfolio
                                        </span>
                                    </button>
                                )}
                            </div>
                        </motion.div>
                    </div>

                    {/* Hint below card */}
                    <motion.p
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 1.1 }}
                        className="mt-5 text-center text-[11px] md:text-xs text-cyan-200/50 uppercase tracking-[0.3em]"
                    >
                        Portfolio remains accessible · Navigate from sidebar
                    </motion.p>
                </motion.div>
            </div>
        </motion.div>
    );
};

export default FrozenOverlay;
